import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { trackPageView } from "./lib/track";
import CookieBanner from "./components/CookieBanner";
import { bootConsent } from "./lib/consent";

import Landing from "./pages/Landing";
import WaitlistLanding from "./pages/WaitlistLanding";
import LaunchScreen from "./pages/LaunchScreen";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import PostJob from "./pages/PostJob";
import FindTrades from "./pages/FindTrades";
import TradesPersonProfile from "./pages/TradesPersonProfile";
import CustomerDashboard from "./pages/CustomerDashboard";
import TradesDashboard from "./pages/TradesDashboard";
import TraderOnboarding from "./pages/TraderOnboarding";
import { Subscribe, BillingSuccess, BillingCancel } from "./pages/Billing";
import JobDetail from "./pages/JobDetail";
import Messages from "./pages/Messages";
import ProfilePage from "./pages/ProfilePage";
import { Pricing, HowItWorks, Terms, Privacy, ForgotPassword, ResetPassword } from "./pages/StaticPages";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import LocalPage from "./pages/LocalPage";
import AdminDashboard from "./pages/AdminDashboard";
import Demo from "./pages/Demo";
import TraderSubscribeMockup from "./pages/TraderSubscribeMockup";
import TraderFlowSketch from "./pages/TraderFlowSketch";
import PreLaunchGate from "./components/PreLaunchGate";
import DemoBanner from "./components/DemoBanner";
import BlogIndex from "./pages/BlogIndex";
import BlogArticle from "./pages/BlogArticle";

function HashSessionCatcher({ children }) {
  // If returning from Google OAuth with session_id in hash, route to AuthCallback.
  const loc = useLocation();

  // Fire a page-view ping on every route change (excluding admin to avoid skewing).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loc.pathname.startsWith("/admin")) return;
    if (window.location.hash?.includes("session_id=")) return;  // mid-OAuth
    trackPageView(loc.pathname);
  }, [loc.pathname]);

  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function AppRoutes() {
  return (
    <HashSessionCatcher>
      <Routes>
        <Route path="/" element={<WaitlistLanding />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/mockups/trader-subscribe" element={<TraderSubscribeMockup />} />
        <Route path="/mockups/trader-flow" element={<TraderFlowSketch />} />
        <Route path="/app" element={<PreLaunchGate><Landing /></PreLaunchGate>} />
        <Route path="/marketing/launch" element={<LaunchScreen />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<PreLaunchGate><Register /></PreLaunchGate>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/find-trades" element={<FindTrades />} />
        <Route path="/trades/:id" element={<PreLaunchGate><TradesPersonProfile /></PreLaunchGate>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route path="/post-job" element={<PreLaunchGate><ProtectedRoute role="customer"><PostJob /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/dashboard/customer" element={<PreLaunchGate><ProtectedRoute role="customer"><CustomerDashboard /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/dashboard/trader" element={<PreLaunchGate><ProtectedRoute role="tradesperson"><TradesDashboard /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/onboarding/trader" element={<PreLaunchGate><ProtectedRoute role="tradesperson"><TraderOnboarding /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/subscribe" element={<PreLaunchGate><ProtectedRoute role="tradesperson"><Subscribe /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/billing/success" element={<PreLaunchGate><ProtectedRoute role="tradesperson"><BillingSuccess /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/billing/cancel" element={<PreLaunchGate><ProtectedRoute role="tradesperson"><BillingCancel /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/jobs/:id" element={<PreLaunchGate><JobDetail /></PreLaunchGate>} />
        <Route path="/messages" element={<PreLaunchGate><ProtectedRoute><Messages /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/profile" element={<PreLaunchGate><ProtectedRoute><ProfilePage /></ProtectedRoute></PreLaunchGate>} />
        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />

        {/* SEO city × trade landing pages — e.g. /plumbers-in-huddersfield */}
        <Route path="/:localSlug" element={<LocalPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </HashSessionCatcher>
  );
}

export default function App() {
  useEffect(() => { bootConsent(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DemoBanner />
          <AppRoutes />
          <CookieBanner />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
